import { T } from '@/lib/theme'
import { Tooltip, HelpIcon } from '@/components/ui'

/// `before -> after` consequence rows, shown under every action form on Markets and Multiply.
///
/// The format matters more than it looks. This app already computed most of these numbers - see
/// MarketsSection.jsx's `projectedDebtUsd`/`projectedZone`/`dropToLiquidationPct` - but rendered
/// them as warnings that appeared only once a value crossed into the caution zone. A transition
/// that is ALWAYS visible teaches the mechanism on a safe input, so the first time someone sees
/// their liquidation price move is not the first time it is nearly upon them.
///
/// A row with no `after` renders as a plain fact rather than a transition, so the same component
/// serves the resting state (nothing typed yet) without a second code path or a layout jump.

/// Which direction is GOOD for this row, so the delta can be coloured honestly. A rising health
/// factor is good; a rising liquidation price is bad; a changing leverage is neither on its own.
/// Defaulting to 'neutral' means a caller that has not thought about it gets no colour rather than
/// a confidently wrong one.
const TONE = {
  up: { better: 1 },
  down: { better: -1 },
  neutral: { better: 0 },
}

function deltaColor(before, after, good) {
  if (before == null || after == null || !Number.isFinite(before) || !Number.isFinite(after)) return T.text
  const dir = after > before ? 1 : after < before ? -1 : 0
  if (dir === 0) return T.text
  const better = (TONE[good] ?? TONE.neutral).better
  if (better === 0) return T.text
  return dir === better ? T.positive : T.negative
}

export function SimulationRow({ label, help, before, after, format, good = 'neutral', beforeRaw, afterRaw }) {
  // `beforeRaw`/`afterRaw` are the numeric values used for colouring; `before`/`after` are what is
  // displayed. They differ whenever the display is a formatted string the caller built itself.
  const b = beforeRaw ?? (typeof before === 'number' ? before : null)
  const a = afterRaw ?? (typeof after === 'number' ? after : null)
  const changed = after !== undefined && after !== null && String(after) !== String(before)
  const color = changed ? deltaColor(b, a, good) : T.text
  const fmtOne = (v) => (v == null ? '—' : format ? format(v) : String(v))

  return (
    <div style={row}>
      <span style={labelStyle}>
        {label}
        {help && (
          <Tooltip content={<div style={tipStyle}>{help}</div>}>
            <HelpIcon text={help} nativeTitle={false} />
          </Tooltip>
        )}
      </span>
      <span className="mono" style={{ ...valueStyle, color }}>
        {changed ? (
          <>
            <span style={{ color: T.textDim }}>{fmtOne(before)}</span>
            <span style={{ color: T.textDim, margin: '0 6px' }}>→</span>
            {fmtOne(after)}
          </>
        ) : (
          fmtOne(before)
        )}
      </span>
    </div>
  )
}

/// `rows`: `[{ label, help, before, after, format, good }]`. Rendered inside a bordered block so
/// the consequences read as one unit attached to the form above them rather than as loose text.
export function SimulationRows({ rows, style = {} }) {
  const visible = rows.filter(Boolean)
  if (visible.length === 0) return null
  return (
    <div style={{ ...wrap, ...style }}>
      {visible.map((r) => <SimulationRow key={r.label} {...r} />)}
    </div>
  )
}

const wrap = {
  marginTop: 14, padding: '4px 0', borderTop: `1px solid ${T.border}`,
}
const row = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '7px 0',
}
const labelStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption,
}
const valueStyle = {
  fontFamily: T.fontN, fontSize: T.fs.caption, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap',
}
const tipStyle = {
  maxWidth: 260, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption,
  lineHeight: 1.5, whiteSpace: 'normal',
}
